import auth from '@udacity/ureact-hoth';

import { getAnonymousIdCookie } from '~/features/cookies/utils/get-anonymous-id-cookie';
import { getCouponCodeFromCookie } from '~/features/discount/utils/get-coupon-code-from-cookie';
import { publicEnv } from '~/features/environment/public';
import { Currency } from '~/features/internationalization/models/currency';
import { logger } from '~/features/logging/logger';

import { BraavosProgramPriceResponse, braavosProgramPriceResponseSchema } from '../models/braavos-program-price';

const PATH_TO_MODULE = 'src/features/plans/controllers/get-program-price.ts';
const FUNCTION_NAME = 'getProgramPrice';

export async function getStandaloneProgramPrice({
  currency,
  nodeKey,
  learningPlanKey,
}: {
  currency: Currency;
  nodeKey?: string;
  learningPlanKey?: string;
}): Promise<BraavosProgramPriceResponse> {
  const url = new URL(`${publicEnv.NEXT_PUBLIC_BRAAVOS_URL}/prices`);
  const anonymousId = getAnonymousIdCookie();
  const userId = auth.getCurrentUserId();
  const coupon = getCouponCodeFromCookie();
  url.searchParams.set('currency', currency);
  if (nodeKey) {
    url.searchParams.set('node_key', nodeKey);
    url.searchParams.set('type', 'EnrollmentSku');
  } else if (learningPlanKey) {
    url.searchParams.set('learning_plan_key', learningPlanKey);
    url.searchParams.set('type', 'LearningPlanSku');
  }
  url.searchParams.set('payment_plan_type', 'one_time');
  if (anonymousId) {
    url.searchParams.set('anonymous_id', anonymousId);
  }
  if (userId) {
    url.searchParams.set('user_id', userId);
  }
  if (coupon) {
    url.searchParams.set('coupon', coupon);
  }
  const response = await fetch(url.toString(), {
    headers: {
      'Content-Type': 'application/json',
    },
  });

  const data = await response.json().catch(() => {});
  if (!response.ok) {
    logger.fatal('Error fetching price plans', {
      file: PATH_TO_MODULE,
      function: FUNCTION_NAME,
      statusCode: response.status,
      error: data,
    });

    throw new Error(`Error fetching price plans: ${JSON.stringify(data)}`);
  }

  try {
    return braavosProgramPriceResponseSchema.parse(data);
  } catch (error) {
    logger.fatal('Error parsing price plans', {
      file: PATH_TO_MODULE,
      function: FUNCTION_NAME,
      error: error,
    });

    // Rethrow so that the error is caught by react-query
    throw error;
  }
}
